import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";

export default function About() {
  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO
        title="About"
        description="Filmcable is a film media publisher delivering authoritative coverage of cinema, film festivals, and the global film industry."
        url="https://filmcable.net/about"
      />
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 border-b border-zinc-200">
        <div className="container">
          <div className="max-w-4xl">
            <span className="font-accent text-sm font-bold uppercase tracking-widest text-[#FF66C4] mb-4 block">
              About
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-none mb-8">
              The Voice of Global Cinema
            </h1>
            <div className="accent-line mb-8" />
            <p className="text-xl md:text-2xl text-zinc-600 leading-relaxed">
              Filmcable is a film media publisher delivering authoritative coverage of 
              cinema, film festivals, and the global film industry.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
              <div className="accent-line mb-8" />
              <div className="space-y-6 text-lg text-zinc-600 leading-relaxed">
                <p>
                  Founded with a commitment to excellence in film journalism, Filmcable 
                  serves as a trusted source for industry professionals, film enthusiasts, 
                  and cinema audiences worldwide.
                </p>
                <p>
                  We believe in the power of cinema to illuminate the human experience, 
                  bridge cultures, and inspire generations. Our editorial mission is to 
                  cover the art and business of film with integrity, insight, and passion.
                </p>
                <p>
                  From the red carpets of Cannes and Venice to the emerging voices of 
                  independent cinema, Filmcable provides comprehensive coverage that 
                  honors the craft and celebrates the storytellers.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1669067978249-f941c66a1ea0?w=800"
                alt="Cinema"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#FF66C4]" />
            </div>
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-20 md:py-28 bg-zinc-50">
        <div className="container">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">What We Cover</h2>
          <div className="accent-line mb-12" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <h3 className="font-serif text-2xl font-bold">Film Festivals</h3>
              <p className="text-zinc-600 leading-relaxed">
                Comprehensive coverage of major international film festivals including 
                Cannes, Venice, Berlin, Toronto, Sundance, and beyond. From premieres 
                to awards, we're on the ground delivering exclusive insights.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="font-serif text-2xl font-bold">Industry News</h3>
              <p className="text-zinc-600 leading-relaxed">
                Breaking news and analysis of the global film industry. Studio 
                developments, distribution trends, production insights, and the 
                business of cinema worldwide.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="font-serif text-2xl font-bold">Editorial Features</h3>
              <p className="text-zinc-600 leading-relaxed">
                In-depth features, interviews, and critical analysis. We explore the 
                art of filmmaking, celebrate emerging talent, and examine the cultural 
                impact of cinema.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Our Values</h2>
            <div className="accent-line mx-auto mb-12" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div className="p-8 border border-zinc-200">
                <h3 className="font-serif text-xl font-bold mb-3">Editorial Integrity</h3>
                <p className="text-zinc-600">
                  Independent journalism free from commercial influence. Our coverage 
                  is driven by editorial merit, not advertising relationships.
                </p>
              </div>
              <div className="p-8 border border-zinc-200">
                <h3 className="font-serif text-xl font-bold mb-3">Global Perspective</h3>
                <p className="text-zinc-600">
                  Cinema is a universal language. We cover films and filmmakers from 
                  every corner of the world, amplifying diverse voices.
                </p>
              </div>
              <div className="p-8 border border-zinc-200">
                <h3 className="font-serif text-xl font-bold mb-3">Professional Excellence</h3>
                <p className="text-zinc-600">
                  Rigorous reporting standards and thoughtful analysis. We respect 
                  the craft of filmmaking and the intelligence of our readers.
                </p>
              </div>
              <div className="p-8 border border-zinc-200">
                <h3 className="font-serif text-xl font-bold mb-3">Industry Access</h3>
                <p className="text-zinc-600">
                  Trusted by studios, festivals, and filmmakers. Our credentials 
                  reflect years of professional relationships built on mutual respect.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 border-t border-zinc-200">
        <div className="container text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">
            Work With Us
          </h2>
          <p className="text-lg text-zinc-600 max-w-2xl mx-auto mb-10">
            For press inquiries, media partnerships, and festival accreditation, 
            please contact our editorial team.
          </p>
          <Link
            to="/press"
            className="inline-flex items-center gap-2 bg-[#FF66C4] text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#e55ab0] transition-colors"
            data-testid="about-press-btn"
          >
            Press & Contact
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
